/**
 * get_expression_help tool - Power Automate expression assistance
 *
 * Provides:
 * - Common function reference
 * - Context-specific examples
 * - Expression syntax validation
 * - Best practices
 */

import { z } from "zod";
import { logger } from "../utils/logger.js";

export const expressionHelpInputSchema = z.object({
  context: z
    .enum(["text", "date", "number", "array", "object", "condition", "email", "general"])
    .optional()
    .default("general")
    .describe("The context for expression help"),
  expression: z
    .string()
    .optional()
    .describe("An expression to validate/explain"),
  question: z
    .string()
    .optional()
    .describe("A specific question about expressions"),
});

export type ExpressionHelpInput = z.infer<typeof expressionHelpInputSchema>;

export const expressionHelpTool = {
  name: "get_expression_help",
  description: `Get help with Power Automate expressions.

Provides:
- Common functions reference by category
- Context-specific examples
- Expression syntax validation
- Best practices and tips

Use when building flow actions that need expressions.`,
  inputSchema: {
    type: "object" as const,
    properties: {
      context: {
        type: "string",
        enum: ["text", "date", "number", "array", "object", "condition", "email", "general"],
        description: "The context for expression help (default: general)",
      },
      expression: {
        type: "string",
        description: "An expression to validate/explain",
      },
      question: {
        type: "string",
        description: "A specific question about expressions",
      },
    },
  },
};

// Expression function reference organized by category
const EXPRESSION_REFERENCE: Record<string, { title: string; functions: Array<{ name: string; desc: string; example: string }> }> = {
  text: {
    title: "Text/String Functions",
    functions: [
      { name: "concat(str1, str2, ...)", desc: "Combine strings", example: "concat('Hello ', variables('name'))" },
      { name: "substring(str, start, length)", desc: "Extract part of string", example: "substring(variables('text'), 0, 10)" },
      { name: "replace(str, old, new)", desc: "Replace text", example: "replace(variables('text'), 'old', 'new')" },
      { name: "split(str, delimiter)", desc: "Split into array", example: "split(variables('csv'), ',')" },
      { name: "toLower(str)", desc: "Convert to lowercase", example: "toLower(triggerBody()?['email'])" },
      { name: "toUpper(str)", desc: "Convert to uppercase", example: "toUpper(variables('code'))" },
      { name: "trim(str)", desc: "Remove whitespace", example: "trim(triggerBody()?['input'])" },
      { name: "length(str)", desc: "Get string length", example: "length(variables('text'))" },
      { name: "indexOf(str, search)", desc: "Find position", example: "indexOf(variables('text'), '@')" },
      { name: "startsWith(str, prefix)", desc: "Check if starts with", example: "startsWith(variables('email'), 'admin')" },
      { name: "endsWith(str, suffix)", desc: "Check if ends with", example: "endsWith(variables('file'), '.pdf')" },
      { name: "contains(str, search)", desc: "Check if contains", example: "contains(variables('text'), 'error')" },
    ],
  },
  date: {
    title: "Date/Time Functions",
    functions: [
      { name: "utcNow()", desc: "Current UTC time", example: "utcNow()" },
      { name: "addDays(date, days)", desc: "Add days", example: "addDays(utcNow(), 7)" },
      { name: "addHours(date, hours)", desc: "Add hours", example: "addHours(utcNow(), 24)" },
      { name: "addMinutes(date, mins)", desc: "Add minutes", example: "addMinutes(utcNow(), 30)" },
      { name: "formatDateTime(date, format)", desc: "Format date", example: "formatDateTime(utcNow(), 'yyyy-MM-dd')" },
      { name: "parseDateTime(str)", desc: "Parse date string", example: "parseDateTime(variables('dateStr'))" },
      { name: "dayOfWeek(date)", desc: "Get day number (0-6)", example: "dayOfWeek(utcNow())" },
      { name: "dayOfMonth(date)", desc: "Get day of month", example: "dayOfMonth(utcNow())" },
      { name: "startOfDay(date)", desc: "Get start of day", example: "startOfDay(utcNow())" },
      { name: "startOfMonth(date)", desc: "Get start of month", example: "startOfMonth(utcNow())" },
      { name: "ticks(date)", desc: "Get ticks (for comparison)", example: "ticks(utcNow())" },
      { name: "convertTimeZone(date, from, to)", desc: "Convert timezone", example: "convertTimeZone(utcNow(), 'UTC', 'Central Standard Time')" },
    ],
  },
  number: {
    title: "Number/Math Functions",
    functions: [
      { name: "add(num1, num2)", desc: "Add numbers", example: "add(variables('a'), variables('b'))" },
      { name: "sub(num1, num2)", desc: "Subtract", example: "sub(variables('total'), variables('discount'))" },
      { name: "mul(num1, num2)", desc: "Multiply", example: "mul(variables('price'), variables('qty'))" },
      { name: "div(num1, num2)", desc: "Divide", example: "div(variables('total'), 100)" },
      { name: "mod(num1, num2)", desc: "Modulo (remainder)", example: "mod(variables('num'), 2)" },
      { name: "min(num1, num2, ...)", desc: "Get minimum", example: "min(variables('a'), variables('b'), 0)" },
      { name: "max(num1, num2, ...)", desc: "Get maximum", example: "max(variables('a'), variables('b'))" },
      { name: "int(value)", desc: "Convert to integer", example: "int(triggerBody()?['amount'])" },
      { name: "float(value)", desc: "Convert to float", example: "float(variables('str'))" },
      { name: "rand(min, max)", desc: "Random number", example: "rand(1, 100)" },
    ],
  },
  array: {
    title: "Array/Collection Functions",
    functions: [
      { name: "length(array)", desc: "Get array length", example: "length(body('Get_items')?['value'])" },
      { name: "first(array)", desc: "Get first item", example: "first(body('Get_items')?['value'])" },
      { name: "last(array)", desc: "Get last item", example: "last(variables('items'))" },
      { name: "take(array, count)", desc: "Take first N items", example: "take(variables('items'), 5)" },
      { name: "skip(array, count)", desc: "Skip first N items", example: "skip(variables('items'), 10)" },
      { name: "contains(array, item)", desc: "Check if contains", example: "contains(variables('list'), 'value')" },
      { name: "union(arr1, arr2)", desc: "Combine arrays (unique)", example: "union(variables('list1'), variables('list2'))" },
      { name: "intersection(arr1, arr2)", desc: "Common items", example: "intersection(variables('list1'), variables('list2'))" },
      { name: "join(array, delimiter)", desc: "Join to string", example: "join(variables('items'), ', ')" },
      { name: "createArray(item1, item2, ...)", desc: "Create array", example: "createArray('a', 'b', 'c')" },
      { name: "empty(array)", desc: "Check if empty", example: "empty(body('Get_items')?['value'])" },
    ],
  },
  object: {
    title: "Object/JSON Functions",
    functions: [
      { name: "json(str)", desc: "Parse JSON string", example: "json(body('HTTP')?['content'])" },
      { name: "string(value)", desc: "Convert to string", example: "string(variables('obj'))" },
      { name: "coalesce(val1, val2, ...)", desc: "First non-null", example: "coalesce(triggerBody()?['name'], 'Unknown')" },
      { name: "if(condition, true, false)", desc: "Conditional value", example: "if(equals(variables('status'), 'active'), 'Yes', 'No')" },
      { name: "null", desc: "Null value", example: "if(empty(variables('x')), null, variables('x'))" },
      { name: "addProperty(obj, key, val)", desc: "Add property", example: "addProperty(variables('obj'), 'newKey', 'value')" },
      { name: "removeProperty(obj, key)", desc: "Remove property", example: "removeProperty(variables('obj'), 'keyToRemove')" },
      { name: "setProperty(obj, key, val)", desc: "Set property", example: "setProperty(variables('obj'), 'key', 'newValue')" },
    ],
  },
  condition: {
    title: "Logical/Condition Functions",
    functions: [
      { name: "equals(val1, val2)", desc: "Check equality", example: "equals(variables('status'), 'approved')" },
      { name: "greater(val1, val2)", desc: "Check if greater", example: "greater(variables('amount'), 1000)" },
      { name: "less(val1, val2)", desc: "Check if less", example: "less(variables('count'), 10)" },
      { name: "greaterOrEquals(v1, v2)", desc: "Greater or equal", example: "greaterOrEquals(variables('score'), 70)" },
      { name: "lessOrEquals(v1, v2)", desc: "Less or equal", example: "lessOrEquals(variables('age'), 18)" },
      { name: "and(cond1, cond2)", desc: "Logical AND", example: "and(equals(a, 1), equals(b, 2))" },
      { name: "or(cond1, cond2)", desc: "Logical OR", example: "or(equals(status, 'a'), equals(status, 'b'))" },
      { name: "not(condition)", desc: "Logical NOT", example: "not(equals(variables('flag'), true))" },
      { name: "if(cond, true, false)", desc: "Ternary if", example: "if(greater(amount, 100), 'High', 'Low')" },
      { name: "empty(value)", desc: "Check if empty/null", example: "empty(triggerBody()?['optionalField'])" },
    ],
  },
  email: {
    title: "Email-Specific Patterns",
    functions: [
      { name: "triggerBody()", desc: "Get email body/trigger data", example: "triggerBody()?['from']" },
      { name: "triggerBody()?['subject']", desc: "Get email subject", example: "triggerBody()?['subject']" },
      { name: "triggerBody()?['body']", desc: "Get email body", example: "triggerBody()?['body']" },
      { name: "triggerBody()?['from']", desc: "Get sender", example: "triggerBody()?['from']" },
      { name: "triggerBody()?['to']", desc: "Get recipients", example: "triggerBody()?['to']" },
      { name: "triggerBody()?['attachments']", desc: "Get attachments", example: "triggerBody()?['attachments']" },
      { name: "base64(content)", desc: "Encode for attachment", example: "base64(body('Get_file_content'))" },
      { name: "base64ToString(b64)", desc: "Decode base64", example: "base64ToString(attachment?['contentBytes'])" },
    ],
  },
  general: {
    title: "Common Data Access",
    functions: [
      { name: "triggerBody()", desc: "Get trigger data", example: "triggerBody()?['fieldName']" },
      { name: "triggerOutputs()", desc: "Get trigger outputs", example: "triggerOutputs()?['headers']['x-ms-correlation-id']" },
      { name: "body('ActionName')", desc: "Get action output", example: "body('Get_item')?['Title']" },
      { name: "outputs('ActionName')", desc: "Get action outputs", example: "outputs('HTTP')?['headers']" },
      { name: "items('Apply_to_each')", desc: "Current loop item", example: "items('Apply_to_each')?['id']" },
      { name: "variables('varName')", desc: "Get variable value", example: "variables('myVariable')" },
      { name: "parameters('paramName')", desc: "Get parameter", example: "parameters('siteUrl')" },
      { name: "workflow()", desc: "Get workflow info", example: "workflow()?['run']?['name']" },
      { name: "result('ScopeName')", desc: "Get scope results", example: "result('Try_scope')" },
      { name: "actions('ActionName')", desc: "Get action info", example: "actions('Send_email')?['status']" },
    ],
  },
};

const BEST_PRACTICES = [
  "Always use ?[] for optional properties: triggerBody()?['field'] instead of triggerBody()['field']",
  "Use coalesce() for default values: coalesce(triggerBody()?['name'], 'Unknown')",
  "Use Compose actions to test complex expressions before using them",
  "Break complex expressions into multiple Compose actions for readability",
  "Use variables for values needed multiple times",
  "Date formats: 'yyyy-MM-dd' for ISO, 'MM/dd/yyyy' for US, 'dd/MM/yyyy' for EU",
  "Test expressions with sample data before deploying",
];

function validateExpression(expr: string): { valid: boolean; issues: string[] } {
  const issues: string[] = [];

  // Check for common mistakes
  if (expr.includes("triggerBody()['") && !expr.includes("?[")) {
    issues.push("Use ?[] for optional access: triggerBody()?['field'] is safer");
  }

  // Check for unbalanced parentheses
  const openParens = (expr.match(/\(/g) || []).length;
  const closeParens = (expr.match(/\)/g) || []).length;
  if (openParens !== closeParens) {
    issues.push(`Unbalanced parentheses: ${openParens} open, ${closeParens} close`);
  }

  // Check for unbalanced brackets
  const openBrackets = (expr.match(/\[/g) || []).length;
  const closeBrackets = (expr.match(/\]/g) || []).length;
  if (openBrackets !== closeBrackets) {
    issues.push(`Unbalanced brackets: ${openBrackets} open, ${closeBrackets} close`);
  }

  return { valid: issues.length === 0, issues };
}

// eslint-disable-next-line @typescript-eslint/require-await -- Conforms to the shared tool-handler signature, which is uniformly async.
export async function handleExpressionHelp(
  input: unknown
): Promise<string> {
  const parsed = expressionHelpInputSchema.parse(input);

  logger.info({ context: parsed.context }, "Providing expression help");

  let output = "";

  // If an expression was provided, validate it
  if (parsed.expression) {
    const validation = validateExpression(parsed.expression);
    output += `## Expression Validation\n\n`;
    output += `**Expression:** \`${parsed.expression}\`\n\n`;

    if (validation.valid) {
      output += `✅ **Syntax looks valid**\n\n`;
    } else {
      output += `⚠️ **Potential Issues:**\n`;
      validation.issues.forEach(issue => {
        output += `  • ${issue}\n`;
      });
      output += `\n`;
    }
  }

  // If a specific question was asked, provide targeted help
  if (parsed.question) {
    const lower = parsed.question.toLowerCase();
    output += `## Answer\n\n`;

    if (lower.includes("null") || lower.includes("empty")) {
      output += `To handle null/empty values:\n`;
      output += `- Check with: \`empty(value)\` or \`equals(value, null)\`\n`;
      output += `- Default value: \`coalesce(value, 'default')\`\n`;
      output += `- Conditional: \`if(empty(value), 'fallback', value)\`\n\n`;
    } else if (lower.includes("date") || lower.includes("time")) {
      output += `For date/time operations:\n`;
      output += `- Current time: \`utcNow()\`\n`;
      output += `- Format: \`formatDateTime(utcNow(), 'yyyy-MM-dd HH:mm')\`\n`;
      output += `- Add days: \`addDays(utcNow(), 7)\`\n`;
      output += `- Compare: \`greater(ticks(date1), ticks(date2))\`\n\n`;
    } else if (lower.includes("loop") || lower.includes("array")) {
      output += `For loops and arrays:\n`;
      output += `- Current item: \`items('Apply_to_each')\`\n`;
      output += `- Item property: \`items('Apply_to_each')?['propertyName']\`\n`;
      output += `- Array length: \`length(body('Get_items')?['value'])\`\n`;
      output += `- Check if empty: \`empty(body('Get_items')?['value'])\`\n\n`;
    } else if (lower.includes("trigger") || lower.includes("input")) {
      output += `To access trigger data:\n`;
      output += `- Body: \`triggerBody()\`\n`;
      output += `- Specific field: \`triggerBody()?['fieldName']\`\n`;
      output += `- Headers: \`triggerOutputs()?['headers']\`\n`;
      output += `- For forms: \`triggerBody()?['responder']\`\n\n`;
    } else {
      output += `See the function reference below for help with "${parsed.question}".\n\n`;
    }
  }

  // Show context-specific reference
  const contextData = EXPRESSION_REFERENCE[parsed.context] ?? EXPRESSION_REFERENCE.general!;

  output += `## ${contextData.title}\n\n`;
  output += `| Function | Description | Example |\n`;
  output += `|----------|-------------|----------|\n`;

  for (const func of contextData.functions) {
    output += `| \`${func.name}\` | ${func.desc} | \`${func.example}\` |\n`;
  }

  // Add best practices
  output += `\n## Best Practices\n\n`;
  BEST_PRACTICES.forEach(practice => {
    output += `• ${practice}\n`;
  });

  // Add quick reference for data access if general context
  if (parsed.context === "general" || !parsed.expression) {
    output += `\n## Quick Reference: Accessing Data\n\n`;
    output += `| Source | Syntax |\n`;
    output += `|--------|--------|\n`;
    output += `| Trigger body | \`triggerBody()?['fieldName']\` |\n`;
    output += `| Action output | \`body('ActionName')?['property']\` |\n`;
    output += `| Loop item | \`items('Apply_to_each')?['property']\` |\n`;
    output += `| Variable | \`variables('varName')\` |\n`;
    output += `| Previous action status | \`actions('ActionName')?['status']\` |\n`;
  }

  return output;
}
